import React, { useState } from 'react';
import { Button, Container, Title, Text, Stack, Alert, Progress, Paper, Group } from '@mantine/core';
import { IconArrowLeft, IconDownload, IconUsers, IconAlertCircle } from '@tabler/icons-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import getUsersList from '../api_scripts/get/getUsersList';

const UserListExport = ({ onBackToTools, authData }) => {
  const [progress, setProgress] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    setExportComplete(false);
    setProgress([]);

    try {
      // Add initial progress message
      setProgress(prev => [
        ...prev,
        { type: 'info', message: 'Starting user export...', timestamp: new Date().toLocaleTimeString() }
      ]);

      // Fetch users list using the existing API script
      const users = await getUsersList(null, authData.org_id, authData.access_token, setProgress);

      if (!users || users.length === 0) {
        setProgress(prev => [
          ...prev,
          { type: 'warning', message: 'No users found to export', timestamp: new Date().toLocaleTimeString() }
        ]);
        setIsExporting(false);
        return;
      }

      setProgress(prev => [
        ...prev,
        { type: 'success', message: `Retrieved ${users.length} users`, timestamp: new Date().toLocaleTimeString() }
      ]);

      // Create Excel workbook
      setProgress(prev => [
        ...prev,
        { type: 'info', message: 'Creating Excel workbook...', timestamp: new Date().toLocaleTimeString() }
      ]);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Users');

      // Define columns based on typical WxCC user properties
      worksheet.columns = [
        { header: 'User ID', key: 'id', width: 40 },
        { header: 'Name', key: 'name', width: 30 },
        { header: 'First Name', key: 'firstName', width: 20 },
        { header: 'Last Name', key: 'lastName', width: 20 },
        { header: 'Email', key: 'email', width: 35 },
        { header: 'Status', key: 'status', width: 15 },
        { header: 'Agent Profile ID', key: 'agentProfileId', width: 40 },
        { header: 'Multimedia Profile ID', key: 'multimediaProfileId', width: 40 },
        { header: 'Site ID', key: 'siteId', width: 40 },
        { header: 'Team ID', key: 'teamId', width: 40 },
        { header: 'Type', key: 'type', width: 20 },
        { header: 'Created Time', key: 'createdTime', width: 25 },
        { header: 'Last Modified Time', key: 'lastModifiedTime', width: 25 }
      ];

      // Style header row
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1976D2' }
      };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      // Add user data
      users.forEach(user => {
        worksheet.addRow({
          id: user.id || '',
          name: user.name || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          status: user.status || '',
          agentProfileId: user.agentProfileId || '',
          multimediaProfileId: user.multimediaProfileId || '',
          siteId: user.siteId || '',
          teamId: user.teamId || '',
          type: user.type || '',
          createdTime: user.createdTime || '',
          lastModifiedTime: user.lastModifiedTime || ''
        });
      });

      // Apply zebra striping
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1 && rowNumber % 2 === 0) {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF5F5F5' }
            };
          });
        }
      });

      // Generate Excel file
      setProgress(prev => [
        ...prev,
        { type: 'info', message: 'Generating Excel file...', timestamp: new Date().toLocaleTimeString() }
      ]);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `WxCC_Users_Export_${timestamp}.xlsx`;

      saveAs(blob, filename);

      setProgress(prev => [
        ...prev,
        { type: 'success', message: `Export complete! Downloaded ${filename}`, timestamp: new Date().toLocaleTimeString() }
      ]);

      setExportComplete(true);

    } catch (error) {
      console.error('Error exporting users:', error);
      setProgress(prev => [
        ...prev,
        { type: 'error', message: `Export failed: ${error.message}`, timestamp: new Date().toLocaleTimeString() }
      ]);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to determine alert color based on message type
  const getAlertColor = (type) => {
    switch (type) {
      case 'success': return 'green';
      case 'error': return 'red';
      case 'warning': return 'yellow';
      case 'info':
      default: return 'blue';
    }
  };

  return (
    <Container size="md" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
      <Stack gap="xl">
        {/* Header */}
        <Paper shadow="sm" p="xl" radius="md" withBorder>
          <Stack gap="md">
            <Group>
              <IconUsers size={48} color="#1976D2" />
              <div>
                <Title order={2} style={{ color: '#1976D2' }}>User List Export</Title>
                <Text size="sm" color="dimmed">
                  Export all WxCC users to an Excel spreadsheet
                </Text>
              </div>
            </Group>

            {authData && (
              <Alert color="blue" variant="light">
                <Text size="sm">
                  <strong>Organization ID:</strong> {authData.org_id}
                </Text>
              </Alert>
            )}
          </Stack>
        </Paper>

        {/* Instructions */}
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="sm">
            <Title order={4}>Instructions</Title>
            <Text size="sm">
              1. Click the "Export Users" button below to fetch all users from your WxCC organization
            </Text>
            <Text size="sm">
              2. The tool will retrieve all users using the WxCC API
            </Text>
            <Text size="sm">
              3. An Excel file will be automatically generated and downloaded with all user information
            </Text>
            <Text size="sm">
              4. The export includes user details such as name, email, status, profiles, site, and team assignments
            </Text>
          </Stack>
        </Paper>

        {/* Export Button */}
        <Group justify="center">
          <Button
            size="lg"
            leftSection={<IconDownload size={20} />}
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting || !authData}
            color="blue"
          >
            {isExporting ? 'Exporting Users...' : 'Export Users'}
          </Button>
        </Group>

        {/* Progress Messages */}
        {progress.length > 0 && (
          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Stack gap="xs">
              <Title order={4}>Progress</Title>
              {progress.map((msg, index) => (
                <Alert
                  key={index}
                  color={getAlertColor(msg.type)}
                  variant="light"
                  icon={msg.type === 'error' ? <IconAlertCircle size={16} /> : null}
                >
                  <Group justify="space-between">
                    <Text size="sm">{msg.message}</Text>
                    <Text size="xs" color="dimmed">{msg.timestamp}</Text>
                  </Group>
                </Alert>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Export Progress Bar */}
        {isExporting && (
          <Progress
            value={100}
            animated
            color="blue"
            size="lg"
            radius="xl"
          />
        )}

        {/* Back Button */}
        <Group justify="center" mt="xl">
          <Button
            variant="light"
            leftSection={<IconArrowLeft size={16} />}
            onClick={onBackToTools}
            color="gray"
          >
            Back to Tools
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default UserListExport;
